import React, { createRef, ReactElement, useCallback, useLayoutEffect, useMemo, useReducer, useRef } from 'react';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import cn from 'classnames';
import { DateTime, Interval } from 'luxon';
import './CalendarNoControls.scss';
import { useLuxonTimezone } from 'hooks';
import { clamp, formatDurationShortDt, intervalsOverlap, mapValue, startOfUSWeek } from 'services/Utils';

export interface ICalendarEvent<TData> {
    id: string,
    groupId?: string,
    // The start of the event. Expected to be in the browser's default timezone.
    start: Date,
    // The end of the event. Expected to be in the browser's default timezone.
    end: Date,
    data?: TData,

    // Whether this event has been autogenerated to fill a gap between grouped events. 
    isGroupGap?: boolean,
    // The event before this gap. Only present on events generated by filling group gaps.
    groupGapPrevEvent?: ICalendarEvent<TData>,
    // The event after this gap. Only present on events generated by filling group gaps.
    groupGapNextEvent?: ICalendarEvent<TData>,
}

export enum EventEdgeStyle {
    // Used for events that continue to another row
    Edge = 'Edge',
    // Default style - offset slightly from the cell's edge
    Offset = 'Offset',
    // Relative to the time within the day. Example, an event starting at 6am is 25% through the day,
    // so it will be positioned 25% from the left edge of the cell.
    Percent = 'Percent',
}

// Since we may render the same event across multiple rows, we split it into internalEvents.
export interface ICalendarEventPart<TData> {
    edgeStyleStart: EventEdgeStyle,
    edgeStyleEnd: EventEdgeStyle,
    formattedDuration: string,
    formattedStartTime: string,
    id: string,
    // The index of the cell on this row in which this event starts. Goes 0 to 6.
    indexCellStart: number,
    // The index of the cell on this row in which this event ends. Goes 0 to 6.
    indexCellEnd: number,
    origEvent: ICalendarEvent<TData>,
    slotIndex: number,

    // The start of the event represented as a luxon DateTime
    startDt: DateTime,
    // The end of the event represented as a luxon DateTime
    endDt: DateTime,

    // ----- Status Classes -----

    isGroupHovered?: boolean,
    isHidden?: boolean,
    isHovered?: boolean,
    // Is this eventPart the first displayed part of its group
    isFirstPartOfGroup?: boolean,
    // Is this eventPart the first displayed part of the original event
    isFirstPartOfEvent?: boolean,
    // Is this eventPart the last displayed part of its group
    isLastPartOfGroup?: boolean,
    // Is this eventPart the last displayed part of the original event
    isLastPartOfEvent?: boolean,
    // Is the start of this event in this row.
    isStartInRow: boolean,
    // Is the end of this event in this row.
    isEndInRow: boolean,
    // Is the left edge of this event connected to an event in the same group
    isLeftEdgeConnected?: boolean,
    // Is the right edge of this event connected to an event in the same group
    isRightEdgeConnected?: boolean,

    // ----- Positioning -----

    top?: number, // Pixels from row top.
    leftPerc?: number, // Percent from row left edge. We're using percent because we won't have to recalc them on resize.
    rightPerc?: number, // Percent from row right edge.
    height?: number, // In pixels
}

export interface ICalendarCell {
    id: string,
    dateDt: DateTime,
    dateEndDt: DateTime,
    isFirstDayOfMonth?: boolean,
    isInCurrentMonth: boolean,
    isToday?: boolean,
}

interface ICalendarMoreButton {
    cellId: string,
    count: number,
    id: string,
    isMoreButton: true,
    leftPerc: number,
    top: number,
}

function isMoreButton<TData>(maybeButton: ICalendarMoreButton | ICalendarEventPart<TData>): maybeButton is ICalendarMoreButton {
    return (maybeButton as ICalendarMoreButton).isMoreButton;
}

interface IGroup<TData> {
    startDt: DateTime,
    endDt: DateTime,
    id: string,
    events: ICalendarEvent<TData>[],
}

type SlotsByCellId<TData> = {
    [cellId: string]: {
        [slotId: number]: ICalendarEventPart<TData>[]
    }
}

// Build day names for headers
const dayNamesLong: string[] = [];
const dayNamesShort: string[] = [];
const daysStartOfWeek = startOfUSWeek(DateTime.now());
for (let i = 0; i < 7; i++) {
    const day = daysStartOfWeek.plus({ days: i });
    dayNamesShort.push(day.toLocaleString({ weekday: 'short' }));
    dayNamesLong.push(day.toLocaleString({ weekday: 'long' }));
}

function getDateId(dt: DateTime): string {
    return `${dt.year}-${dt.month}-${dt.day}`;
}

// Maps a date to a percent within the day.
// Examples:
//   12:00am = 0
//    6:00am = 0.25
//   12:00pm = 0.5
//    6:00pm = 0.75
//   11:59pm = 0.999
function datePercentWithinCell(d: Date, cell: ICalendarCell): number {
    return mapValue(d.valueOf(), cell.dateDt.valueOf(), cell.dateEndDt.valueOf(), 0, 1, true);
}

// Time formatting, hide minutes if the time is on the hour.
function formatTimeShort(dt: DateTime): string {
    if (!dt || !dt.isValid) return dt + '';

    // If minutes are 0, only show the hour
    if (dt.minute === 0) {
        return dt.toLocaleString({
            hour: 'numeric'
        });
    }

    return dt.toLocaleString({
        hour: 'numeric',
        minute: '2-digit',
    })
}

// Helper to make sure we don't forget to set the timezone when creating new dates.
function NewDateTime(date: Date, timezone: string): DateTime {
    return DateTime.fromJSDate(date, { zone: timezone });
}

// Group events by their groupId, if they have one
function groupEvents<TData>(sortedEvents: ICalendarEvent<TData>[], timezone: string): IGroup<TData>[] {
    const groupsById: {[groupId: string]: IGroup<TData>} = {}
    for (const e of sortedEvents) {
        let groupId = e.groupId || ('__noGroupId__' + e.id);
        let group = groupsById[groupId];
        if (!group) {
            group = {
                id: groupId,
                startDt: NewDateTime(e.start, timezone),
                endDt: NewDateTime(e.end, timezone),
                events: [],
            }
        }
        group.events.push(e);
        if (e.end.valueOf() > group.endDt.valueOf()) {
            group.endDt = NewDateTime(e.end, timezone);
        }
        groupsById[groupId] = group;
    }
    const groups = Object.values(groupsById);
    groups.sort((a, b) => a.startDt.valueOf() - b.startDt.valueOf());
    return groups;
}

// If 2 events within a group overlap, they won't render right, so we need to split the group in that case.
function splitGroupsWithOverlap<TData>(groups: IGroup<TData>[], timezone: string): IGroup<TData>[] {
    const out = [];

    for (const group of groups) {
        if (group.events.length <= 1) {
            out.push(group);
            continue;
        }

        // Rebuild the group starting at the first event. This is slightly inefficient, but it's more understandable
        let newGroup: IGroup<TData> = {
            id: group.id,
            startDt: NewDateTime(group.events[0].start, timezone),
            endDt: NewDateTime(group.events[0].end, timezone),
            events: [group.events[0]],
        }

        // For each event in the existing group, only add it to the new group if it doesn't overlap.
        let overlapGroupId = 1;
        for (let i = 1; i < group.events.length; i++) {
            const e = group.events[i];

            if (e.start.valueOf() >= newGroup.endDt.valueOf()) {
                newGroup.events.push(e);

                if (e.end.valueOf() > newGroup.endDt.valueOf()) {
                    newGroup.endDt = NewDateTime(e.end, timezone);
                }
            } else {
                // Event overlaps, need to start a new group.
                out.push(newGroup);
                overlapGroupId++;
                newGroup = {
                    id: group.id + '__overlap__' + overlapGroupId,
                    startDt: NewDateTime(e.start, timezone),
                    endDt: NewDateTime(e.end, timezone),
                    events: [e],
                }
            }
        }
        out.push(newGroup);
    }
    return out;
}

// This adds "gap" events to any gaps between grouped events.
function fillGroupGaps<TData>(groups: IGroup<TData>[], timezone: string, gapToleranceMinutes: number): void {
    let gapId = 1;

    for (const group of groups) {
        if (group.events.length === 0) continue;

        const newGroupEvents: ICalendarEvent<TData>[] = [];
        // Iterate to second to last event, because we do "i + 1"
        for (let i = 0; i < group.events.length - 1; i++) {
            const cur = group.events[i];
            const next = group.events[i + 1];
            newGroupEvents.push(cur);

            // Add a gap event if the gap is bigger than our tolerance.
            const curEndDt = NewDateTime(cur.end, timezone);
            const nextStartDt = NewDateTime(next.start, timezone);

            if (nextStartDt.diff(curEndDt, 'minutes').minutes > gapToleranceMinutes) {
                newGroupEvents.push({
                    id: '__groupGap__' + (gapId++),
                    groupId: group.id,
                    start: cur.end,
                    end: next.start,
                    data: next.data,
                    isGroupGap: true,
                    groupGapPrevEvent: cur,
                    groupGapNextEvent: next,
                });
            }
        }

        // Since we didn't iterate to the last event, we need to add it here
        newGroupEvents.push(group.events[group.events.length - 1])

        group.events = newGroupEvents;
    }
}

// Returns the index of the first available slot in a cell
function slots_findOpenIndex<T>(slots: SlotsByCellId<T>, cellId: string): number {
    const cellSlots = slots[cellId];
    if (!cellSlots) {
        return 0;
    }

    for (let i = 0; true; i++) {
        const slot = cellSlots[i];
        if (!slot || slot.length === 0) {
            return i;
        }
    }
}

function slots_insertEvent<T>(slots: SlotsByCellId<T>, cellId: string, slotIndex: number, event: ICalendarEventPart<T>) {
    let cellSlots = slots[cellId];
    // Initialize slots for cell
    if (!cellSlots) {
        cellSlots = {};
        slots[cellId] = cellSlots;
    }

    // Initialize events array for slot
    let slotEvents = cellSlots[slotIndex];
    if (!slotEvents) {
        slotEvents = [];
        cellSlots[slotIndex] = slotEvents;
    }

    slotEvents.push(event);
}

export interface ICalendarRenderProps {
    className?: string,
    key?: string,
    onMouseEnter?: React.MouseEventHandler<HTMLElement>,
    onMouseLeave?: React.MouseEventHandler<HTMLElement>,
    ref?: React.MutableRefObject<any>,
    style?: React.CSSProperties,
}

export type ICalendarRenderEventCallback<TData> = (
    props: ICalendarRenderProps,
    event: ICalendarEvent<TData>,
    eventPart: ICalendarEventPart<TData>,
) => ReactElement;

export type ICalendarRenderDayPanelEventCallback<TData> = (
    props: ICalendarRenderProps,
    event: ICalendarEvent<TData>,
    eventPart: ICalendarEventPart<TData>,
    dayPanelCalendarCell: ICalendarCell,
) => ReactElement;

export interface ICalendarNoControlsProps<TData> {
    className?: string,
    date: Date,
    defaultEventEdgeStyle?: EventEdgeStyle,
    events: ICalendarEvent<TData>[],
    groupGapToleranceMinutes?: number,
    // TODO - nice loading animation
    isLoading?: boolean,
    selectedDate: Date,
    showDayPanel: boolean,
    timezone?: string,

    onClickCell: (cell: ICalendarCell) => void,
    onClickMoreButton: (cell: ICalendarCell) => void,
    onCloseDayPanel: () => void,
    renderEvent: ICalendarRenderEventCallback<TData>,
    renderEventDayPanel: ICalendarRenderDayPanelEventCallback<TData>,
}

export function CalendarNoControls<TData>({
    className,
    date,
    defaultEventEdgeStyle = EventEdgeStyle.Offset,
    events,
    groupGapToleranceMinutes = 1,
    selectedDate,
    showDayPanel,
    timezone: propsTimezone,

    onClickCell,
    onClickMoreButton,
    onCloseDayPanel,
    renderEvent,
    renderEventDayPanel,
}: ICalendarNoControlsProps<TData>) {
    const sizesRef = useRef({
        cellHeaderHeight: 0,
        cellHeight: 0,
        cellShowMoreHeight: 20, // Hardcoded, not dynamically calculated
        cellWidth: 0,
        headersRowHeight: 0,
        height: 0,
        width: 0,

        eventHeights: {},
    });
    const hoveredEventIdRef = useRef<string>(undefined);
    const hoveredGroupIdRef = useRef<string>(undefined);
    const sizes = sizesRef.current;
    const renderPassRef = useRef<1 | 2>(1);
    const calendarRef = useRef<HTMLDivElement>();
    const headerRowRef = useRef<HTMLDivElement>();
    const cellRef = useRef<HTMLDivElement>();
    const cellHeaderRef = useRef<HTMLDivElement>();
    const eventRefs = useRef<{ [eventId: string]: React.MutableRefObject<HTMLDivElement> }>({});
    // https://reactjs.org/docs/hooks-faq.html#is-there-something-like-forceupdate
    const [_, forceUpdate] = useReducer(x => x + 1, 0);

    const { timezone } = useLuxonTimezone(propsTimezone);

    const dateDt = useMemo(() => {
        return DateTime.fromJSDate(date, {zone: timezone})
    }, [date]);

    const selectedDateDt = useMemo(() => {
        if (!selectedDate) return undefined;
        return DateTime.fromJSDate(selectedDate, {zone: timezone});
    }, [selectedDate]);

    // Build calendar cells
    const rows: ICalendarCell[][] = useMemo(() => {
        const monthStartDt = dateDt.startOf('month');
        const todayDt = DateTime.now().setZone(timezone);
        let newRows: ICalendarCell[][] = [];

        // Start on first day on the calendar, which may be in the previous month.
        // Then, add rows until we get out of the current month
        let curDateDt = startOfUSWeek(monthStartDt);
        let isInCurrentMonth = false;
        do {
            const row: ICalendarCell[] = [];
            for (let i = 0; i < 7; i++, curDateDt = curDateDt.plus({days: 1})) {
                isInCurrentMonth = curDateDt.hasSame(dateDt, 'month');

                row.push({
                    id: getDateId(curDateDt),
                    dateDt: curDateDt,
                    dateEndDt: curDateDt.endOf('day'),
                    isFirstDayOfMonth: curDateDt.day === 1,
                    isInCurrentMonth: isInCurrentMonth,
                    isToday: curDateDt.hasSame(todayDt, 'day'),
                });
            }
            newRows.push(row);
        }
        while (curDateDt.plus({days: 1}).hasSame(dateDt, 'month'));

        return newRows;
    }, [date, timezone]);

    const cellsById: {[cellId: string]: ICalendarCell} = useMemo(() => {
        const out = {};
        for (const row of rows) {
            for (const cell of row) {
                out[cell.id] = cell;
            }
        }
        return out;
    }, [rows]);

    const selectedCell = selectedDateDt && cellsById[getDateId(selectedDateDt)];

    function updateContainerSizes() {
        sizes.cellHeaderHeight = cellHeaderRef.current.clientHeight;
        sizes.cellHeight = cellRef.current.clientHeight;
        sizes.cellWidth = cellRef.current.clientWidth;
        sizes.headersRowHeight = headerRowRef.current.clientHeight;
        sizes.height = calendarRef.current.clientHeight;
        sizes.width = calendarRef.current.clientWidth;
    }

    function updateEventSizes() {
        for (const [eventId, ref] of Object.entries(eventRefs.current)) {
            // During loading, the refs may be null
            if (ref.current) {
                sizes.eventHeights[eventId] = ref.current.clientHeight;
            }
        }
    }

    // -----------------------------------------------------------
    // ------------------- Rendering Lifecycle -------------------
    // -----------------------------------------------------------
    // 2 pass render strategy:
    //   Pass 1: Render all events, then run an effect after the render to get the dom sizes.
    //   Pass 2: Hide events that don't fit in each cell and set grouped events to the same height.
    // To prevent unnecessary rerenders, we're using refs instead of state and manually rerendering when we need to.

    // If we have events, rerender so that we can hide the ones that don't fit.
    useLayoutEffect(() => {
        if (renderPassRef.current === 1) {
            updateContainerSizes();
            // During pass 1, all events where rendered as visible so that we can get their sizes.
            updateEventSizes();

            if (events.length > 0) {
                renderPassRef.current = 2;
                forceUpdate();
            }
        }
    });

    // We need to restart our rendering if our events change
    useLayoutEffect(() => {
        if (renderPassRef.current !== 1) {
            renderPassRef.current = 1;
            forceUpdate();
        }
    }, [date, events]);

    // Rerender on resize. However, the events haven't changed, so we don't need to go back to renderPass 1.
    useLayoutEffect(() => {
        function onResize() {
            updateContainerSizes();
            forceUpdate();
        }

        window.addEventListener('resize', onResize);
        return () => window.removeEventListener('resize', onResize);
    }, [])

    // -----------------------------------------------------------
    // ----------------- Split events into rows ------------------
    // -----------------------------------------------------------

    const [eventsPerRow, eventsPerCell, slotsByCellId]: [
        ICalendarEventPart<TData>[][],
        {[cellId: string]: ICalendarEventPart<TData>[]},
        SlotsByCellId<TData>
    ] = useMemo(() => {
        const newEventsPerRow: ICalendarEventPart<TData>[][] = [];
        const newEventsPerCell: {[cellId: string]: ICalendarEventPart<TData>[]} = {};
        const newSlots: SlotsByCellId<TData> = {}
        for (const row of rows) {
            for (const cell of row) {
                newSlots[cell.id] = {};
                newEventsPerCell[cell.id] = [];
            }
        }

        // In order to stack events vertically within a row, we'll use the concept of "slots",
        // where only 1 event can occupy a slot for a day, except for grouped events.
        //
        // Algorithm:
        //   Sort events by start date
        //   Group events - this allows us to render stages on the same slot
        //       Include events in a group only if they have the same groupId and don't overlap.
        //       If an event overlaps, start a new group.
        //       We can optionally add gap events to fill gaps between grouped events
        //   For each row:
        //       For each group event:
        //           For each cell:
        //               If event overlaps the cell:
        //                   Take the first available slot on the start day
        //                   Use that slot to fill any remaining days in this row that the event overlaps
        //               
        const sortedEvents = [...events];
        sortedEvents.sort((a, b) => a.start.valueOf() - b.start.valueOf());
        let groups = groupEvents(sortedEvents, timezone);
        groups = splitGroupsWithOverlap(groups, timezone);
        fillGroupGaps(groups, timezone, groupGapToleranceMinutes);

        const eventsByOrigEventId: {[eventId: string]: ICalendarEventPart<TData>[]} = {};
        for (const g of groups) {
            for (const e of g.events) {
                eventsByOrigEventId[e.id] = [];
            }
        }

        const eventsByGroupId: {[eventId: string]: ICalendarEventPart<TData>[]} = {};
        groups.forEach(g => eventsByGroupId[g.id] = []);

        for (let rowI = 0; rowI < rows.length; rowI++) {
            const row = rows[rowI];
            const rowStartDt = row[0].dateDt;
            const rowEndDt = row[row.length - 1].dateEndDt;
            const rowInterval = Interval.fromDateTimes(rowStartDt, rowEndDt);
            const rowEvents: ICalendarEventPart<TData>[] = [];
            const rowEventsByGroupId: {[groupId: string]: ICalendarEventPart<TData>[]} = {};
            groups.forEach(g => rowEventsByGroupId[g.id] = []);

            // Only include events that overlap this row
            for (const g of groups) {
                // All events in this group will go in the same slot
                let slotIndex = -1;

                // NOTE: We're using a half-open overlaps method, which is different than luxon's.
                // See notes on the function.
                // Order matters, always do overlaps(calendarInterval, eventInterval), not the other way around.
                const groupInterval = Interval.fromDateTimes(g.startDt, g.endDt);
                if (!intervalsOverlap(rowInterval, groupInterval)) {
                    continue;
                }

                for (let eventsI = 0; eventsI < g.events.length; eventsI++) {
                    const e = g.events[eventsI];
                    const eStartDt = NewDateTime(e.start, timezone);
                    const eEndDt = NewDateTime(e.end, timezone);
                    const eventInterval = Interval.fromDateTimes(eStartDt, eEndDt);
                    if (!intervalsOverlap(rowInterval, eventInterval)) {
                        continue;
                    }

                    const isStartInRow = eStartDt >= rowStartDt;
                    const isEndInRow = eEndDt <= rowEndDt;

                    // We know this event overlaps this row, so go ahead and create the internal event.
                    const ePart: ICalendarEventPart<TData> = {
                        edgeStyleStart: defaultEventEdgeStyle,
                        edgeStyleEnd: defaultEventEdgeStyle,
                        endDt: eEndDt,
                        formattedDuration: formatDurationShortDt(eStartDt, eEndDt),
                        formattedStartTime: formatTimeShort(eStartDt),
                        id: `row_${rowI}_${e.id}`,
                        indexCellStart: -1,
                        indexCellEnd: 6,
                        isStartInRow: isStartInRow,
                        isEndInRow: isEndInRow,
                        origEvent: e,
                        slotIndex: -1,
                        startDt: eStartDt,
                    }
                    rowEvents.push(ePart);
                    eventsByOrigEventId[e.id].push(ePart);
                    rowEventsByGroupId[e.groupId].push(ePart);
                    eventsByGroupId[e.groupId].push(ePart);

                    if (eStartDt < rowStartDt) {
                        ePart.edgeStyleStart = EventEdgeStyle.Edge;
                    }
                    if (eEndDt > rowEndDt) {
                        ePart.edgeStyleEnd = EventEdgeStyle.Edge;
                    }

                    // Find the cells that this event goes in.
                    for (let cellI = 0; cellI < 7; cellI++) {
                        const cell = row[cellI];
                        const cellInterval = Interval.fromDateTimes(cell.dateDt, cell.dateEndDt);

                        if (intervalsOverlap(cellInterval, eventInterval)) {
                            // The event overlaps this cell, insert it
                            if (slotIndex === -1) {
                                slotIndex = slots_findOpenIndex(newSlots, cell.id);
                            }

                            if (ePart.indexCellStart === -1) {
                                ePart.indexCellStart = cellI;
                            }

                            ePart.slotIndex = slotIndex;
                            slots_insertEvent(newSlots, cell.id, slotIndex, ePart);
                            newEventsPerCell[cell.id].push(ePart);
                        } else {
                            if (ePart.indexCellStart !== -1) {
                                // We've found where this event ends, set its end index and stop.
                                ePart.indexCellEnd = cellI - 1;
                                break;
                            }
                        }
                    }
                }
            }
            newEventsPerRow.push(rowEvents);
        }

        for (const groupedEvents of Object.values(eventsByGroupId)) {
            if (groupedEvents.length > 0) {
                // Mark first and last
                groupedEvents[0].isFirstPartOfGroup = true;
                groupedEvents[groupedEvents.length - 1].isLastPartOfGroup = true;
            }
            if (groupedEvents.length > 1) {
                // Mark connected edges of grouped events
                for (let i = 0; i + 1 < groupedEvents.length; i++) {
                    groupedEvents[i].isRightEdgeConnected = true;
                    groupedEvents[i + 1].isLeftEdgeConnected = true;
                }
            }
        }

        // Mark first and last parts of an event
        for (const eventParts of Object.values(eventsByOrigEventId)) {
            if (eventParts.length > 0) {
                eventParts[0].isFirstPartOfEvent = true;
                eventParts[eventParts.length - 1].isLastPartOfEvent = true;
            }
        }

        // If a slot contains multiple events, we need to set their style to "Percentage" instead of the default.
        // This prevents them from overlapping and is necessary for grouped events.
        for (const cellSlots of Object.values(newSlots)) {
            for (const slot of Object.values(cellSlots)) {
                if (slot.length > 1) {
                    // Events are already sorted, so we can mark all left edges except for the first event,
                    // and all right edges except for the last event.
                    for (let i = 0; i < slot.length; i++) {
                        const event = slot[i];
                        if (i > 0) {
                            event.edgeStyleStart = EventEdgeStyle.Percent;
                        }
                        if (i + 1 < slot.length) {
                            event.edgeStyleEnd = EventEdgeStyle.Percent;
                        }
                    }
                }
            }
        }

        return [newEventsPerRow, newEventsPerCell, newSlots];
    }, [rows, events])

    // -----------------------------------------------------------
    // --------------------- Position Events ---------------------
    // -----------------------------------------------------------

    const slotGapPx = 2;
    const edgeOffsetPerc = 0.25;
    // It looks better if an event's start and end are slightly offset from the cell edge,
    // even if we're doing "Percent" edge style.
    const eventStartEndMinimumOffsetPerc = 0.25;
    const eventMinHeightPx = 20;
    const eventMinVisibleWidthPerc = 2;

    // Find the max height for each group, then set each event in the group to the same height.
    const maxHeightPerGroup: {[groupId: string]: number} = {};
    for (const row of eventsPerRow) {
        for (const e of row) {
            let eventHeight = sizes.eventHeights[e.id] || 0;
            eventHeight = Math.max(eventHeight, eventMinHeightPx)

            const curMaxGroupHeight = maxHeightPerGroup[e.origEvent.groupId] || 0
            maxHeightPerGroup[e.origEvent.groupId] = Math.max(curMaxGroupHeight, eventHeight);
        }
    }
    for (const row of eventsPerRow) {
        for (const e of row) {
            e.height = maxHeightPerGroup[e.origEvent.groupId];
            e.isHovered = (e.origEvent.id === hoveredEventIdRef.current);
            e.isGroupHovered = (e.origEvent.groupId === hoveredGroupIdRef.current);
        }
    }

    const eventsAndButtonsPerRow: Array<Array<ICalendarEventPart<TData> | ICalendarMoreButton>> = [];
    for (let rowI = 0; rowI < eventsPerRow.length; rowI++) {
        const row = eventsPerRow[rowI];
        // Find the height for each slot, it needs to be tall enough to contain all events in the slot.
        const slotHeights: {[slotIndex: number]: number} = {};
        let maxSlotIndex = -1;

        for (const e of row) {
            maxSlotIndex = Math.max(maxSlotIndex, e.slotIndex);
            const curMaxSlotHeight = slotHeights[e.slotIndex] || 0;
            slotHeights[e.slotIndex] = Math.max(curMaxSlotHeight, e.height);
        }

        // Add up slot heights to get the offset for each slot from the top of the cell
        const slotOffsets: {[slotIndex: number]: number} = {};
        let curSlotOffset = sizes.cellHeaderHeight + slotGapPx;

        for (let i = 0; i <= maxSlotIndex; i++) {
            slotOffsets[i] = curSlotOffset;
            curSlotOffset += slotGapPx + (slotHeights[i] || 0);
        }

        // Now that we have slot heights, position each event
        for (const e of row) {
            e.top = slotOffsets[e.slotIndex];
            const leftEdgePerc = (e.indexCellStart / 7) * 100;
            const rightEdgePerc = ((6 - e.indexCellEnd) / 7) * 100;
            const cellStart = rows[rowI]?.[e.indexCellStart];
            const cellEnd = rows[rowI]?.[e.indexCellEnd];

            if (!cellStart || !cellEnd) {
                console.error('Missing cell for event start or end. event, cells: ', e, rows);
                continue;
            }

            // Position left edge
            if (e.edgeStyleStart === EventEdgeStyle.Edge) {
                e.leftPerc = leftEdgePerc;
            } else if (e.edgeStyleStart === EventEdgeStyle.Offset) {
                e.leftPerc = leftEdgePerc + edgeOffsetPerc;
            } else if (e.edgeStyleStart === EventEdgeStyle.Percent) {
                e.leftPerc = leftEdgePerc + (datePercentWithinCell(e.origEvent.start, cellStart) * (1 / 7) * 100);
            }

            // Position right edge
            if (e.edgeStyleEnd === EventEdgeStyle.Edge) {
                e.rightPerc = rightEdgePerc;
            } else if (e.edgeStyleEnd === EventEdgeStyle.Offset) {
                e.rightPerc = rightEdgePerc + edgeOffsetPerc;
            } else if (e.edgeStyleEnd === EventEdgeStyle.Percent) {
                // We need to move the right edge of the event back into the cell, moving left from the edge.
                // CellStart             EventEnd  CellEnd
                // |---------------------]         |
                // |      Event          ]         |
                // |---------------------]         |
                const eventDayPerc = datePercentWithinCell(e.origEvent.end, cellEnd);
                e.rightPerc = rightEdgePerc + ((1 - eventDayPerc) * (1 / 7) * 100);
            }

            // Apply miminum offsets if the event is the first/last in the group.
            if (e.isFirstPartOfGroup && e.isStartInRow) {
                e.leftPerc = Math.max(e.leftPerc, leftEdgePerc + eventStartEndMinimumOffsetPerc);
            }
            if (e.isLastPartOfGroup && e.isEndInRow) {
                e.rightPerc = Math.max(e.rightPerc, rightEdgePerc + eventStartEndMinimumOffsetPerc);
            }

            // Keep events somewhat visible. IE, if it starts at 11:59pm on Saturday, move it left slightly.
            e.leftPerc = clamp(e.leftPerc, 0, 100 - eventMinVisibleWidthPerc);
            // // Apply minwidth
            e.rightPerc = clamp(e.rightPerc, 0, 100 - (e.leftPerc + eventMinVisibleWidthPerc));
        }

        // Sort events in the expected tab order for keyboard nav, left to right, top to bottom.
        // Up until this point, grouped events were together instead.
        row.sort((a, b) => {
            if (a.indexCellStart !== b.indexCellStart) return a.indexCellStart - b.indexCellStart;

            // Events start in the same cell, try comparing slots
            if (a.slotIndex !== b.slotIndex) return a.slotIndex - b.slotIndex;

            // Events are in same slot, use startDate.
            return a.startDt.valueOf() - b.startDt.valueOf();
        });

        // If we're on render pass 2, we have valid height measurements, so we should hide events in slots that don't fit.
        if (renderPassRef.current === 1) {
            eventsAndButtonsPerRow.push(row);
        } else {
            function calcMaxVisibleSlotIndex(availableHeight: number, minVisiblePercent: number, maxIndex: number): number {
                for (let i = 0; i <= maxIndex; i++) {
                    // At least some of this slot must be visible
                    if (slotOffsets[i] + (slotHeights[i] * minVisiblePercent) > availableHeight) {
                        // Doesn't fit, return the previous slot
                        return i - 1;
                    }
                }

                return maxIndex;
            }

            // Require at least 70% of a slot be visible.
            let maxVisibleSlotIndex = calcMaxVisibleSlotIndex(sizes.cellHeight, 0.7, maxSlotIndex);

            // If some slots didn't fit, we need to recalculate, leaving space to show the "show more" button.
            // In this case, 100% of a slot must be visible because we don't want to overlap the button.
            if (maxVisibleSlotIndex < maxSlotIndex) {
                maxVisibleSlotIndex = calcMaxVisibleSlotIndex(sizes.cellHeight - sizes.cellShowMoreHeight, 1, maxSlotIndex);
            }

            // Hide events in slots that don't fit
            for (const e of row) {
                e.isHidden = (e.slotIndex > maxVisibleSlotIndex);
            }

            // Insert "more" buttons for cells in this row that have hidden events.
            let cellHiddenCounts = [0, 0, 0, 0, 0, 0, 0];
            for (const e of row) {
                if (e.isHidden) {
                    // Increment the hidden count for each cell this event is in.
                    for (let i = e.indexCellStart; i <= e.indexCellEnd; i++) {
                        cellHiddenCounts[i]++;
                    }
                }
            }

            const eventsAndButtons: Array<ICalendarEventPart<TData> | ICalendarMoreButton> = [...row];
            let insertedButtonsCount = 0;
            for (let cellI = 0; cellI < 7; cellI++) {
                const count = cellHiddenCounts[cellI];

                if (count > 0) {
                    const moreButton: ICalendarMoreButton = {
                        cellId: rows[rowI][cellI].id,
                        count: count,
                        id: `cell_${cellI}_moreButton`,
                        isMoreButton: true,
                        leftPerc: mapValue(cellI, 0, 6, 0, (6 / 7) * 100),
                        top: slotOffsets[maxVisibleSlotIndex + 1],
                    }

                    // Insert the button at the correct spot, which is at the first event with a higher start index.
                    // Splicing at this index will insert the button before the event.
                    let insertIndex = row.findIndex(e => e.indexCellStart > cellI);
                    if (insertIndex === -1) {
                        // Didn't find an event that goes after this button, so insert at end
                        insertIndex = row.length;
                    }

                    // We're adding to the target array, so use "insertedButtonsCount" to shift our insert index
                    // to account for previously inserted buttons.
                    insertIndex += insertedButtonsCount;
                    eventsAndButtons.splice(insertIndex, 0, moreButton);
                    insertedButtonsCount++;
                }
            }
            eventsAndButtonsPerRow.push(eventsAndButtons);
        }
    }

    // -----------------------------------------------------------
    // ----------------------- DOM Events ------------------------
    // -----------------------------------------------------------

    function onClickCellInternal(e: React.MouseEvent<HTMLElement>, cellId: string) {
        if (e.currentTarget && (e.currentTarget as HTMLElement).contains) {
            // Make sure the click came from the cell, and not a click on an event.
            if ((e.currentTarget as HTMLElement).contains(e.target as HTMLElement)) {
                onClickCell(cellsById[cellId]);
            }
        }
    }

    function onClickMoreButtonInternal(cellId: string) {
        onClickMoreButton(cellsById[cellId]);
    }

    function onMouseEnterEvent(eventPart: ICalendarEventPart<TData>) {
        hoveredEventIdRef.current = eventPart.origEvent.id;
        hoveredGroupIdRef.current = eventPart.origEvent.groupId;
        forceUpdate();
    }

    function onMouseLeaveEvent(eventPart: ICalendarEventPart<TData>) {
        hoveredEventIdRef.current = undefined;
        hoveredGroupIdRef.current = undefined;
        forceUpdate();
    }

    // Dynamically create refs for events, so that we can get their heights later.
    for (const row of eventsPerRow) {
        for (const e of row) {
            if (!eventRefs.current[e.id]) {
                eventRefs.current[e.id] = createRef();
            }
        }
    }

    const generateEventClasses = useCallback((
        e: ICalendarEvent<TData>,
        eInternal: ICalendarEventPart<TData>,
        dayPanelCalendarCell?: ICalendarCell
    ): string => {
        let leftConnected = eInternal.isLeftEdgeConnected;
        let rightConnected = eInternal.isRightEdgeConnected;

        if (dayPanelCalendarCell) {
            leftConnected = e.start.valueOf() < dayPanelCalendarCell.dateDt.valueOf();
            rightConnected = e.end.valueOf() > dayPanelCalendarCell.dateEndDt.valueOf();
        }

        return cn({
            'calendar_event_isFirstPartOfGroup': eInternal.isFirstPartOfGroup,
            'calendar_event_isLastPartOfGroup': eInternal.isLastPartOfGroup,
            'calendar_event_isFirstPartOfEvent': eInternal.isFirstPartOfEvent,
            'calendar_event_isLastPartOfEvent': eInternal.isLastPartOfEvent,
            'calendar_event_isGroupGap': eInternal.origEvent.isGroupGap,
            'calendar_event_isGroupHovered': eInternal.isGroupHovered,
            'calendar_event_isHovered': eInternal.isHovered,
            'calendar_event_isLeftEdgeConnected': leftConnected,
            'calendar_event_isRightEdgeConnected': rightConnected,
            'calendar_event_isStartInRow': eInternal.isStartInRow,
            'calendar_event_isEndInRow': eInternal.isEndInRow,
        });
    }, []);

    return <div className="calendarWrap">
        <div className={cn("calendar", className)} ref={calendarRef}>
            <div className="calendar_headers" ref={headerRowRef}>
                {[0, 1, 2, 3, 4, 5, 6].map(i => (
                    <div className="calendar_header" key={i}>
                        {sizes.width > 700 ? dayNamesLong[i] : dayNamesShort[i]}
                    </div>
                ))}
            </div>
            {rows.map((row, rowI) => (
                <div className="calendar_row" key={rowI}>
                    {row.map((cell, j) => (
                        <div
                            className={cn('calendar_cell', {
                                'calendar_cell_isFirstDayOfMonth': cell.isFirstDayOfMonth,
                                'calendar_cell_isInCurrentMonth': cell.isInCurrentMonth,
                                'calendar_cell_isNotInCurrentMonth': !cell.isInCurrentMonth,
                                'calendar_cell_isSelected': cell === selectedCell,
                                'calendar_cell_isToday': cell.isToday,
                            })}
                            key={j}
                            onClick={(e) => onClickCellInternal(e, cell.id)}
                            ref={(rowI === 0 && j === 0) ? cellRef : null}
                        >
                            <div
                                className="calendar_cellDay"
                                ref={(rowI === 0 && j === 0) ? cellHeaderRef : null}
                            >
                                <span className="calendar_cellDayText">
                                    {cell.isFirstDayOfMonth && cell.dateDt.toLocaleString({ month: 'short', day: 'numeric' })}
                                    {!cell.isFirstDayOfMonth && cell.dateDt.toLocaleString({ day: 'numeric' })}
                                </span>
                            </div>
                        </div>
                    ))}
                    {eventsAndButtonsPerRow[rowI].map((e) => {
                        if (isMoreButton(e)) {
                            return (
                                <div
                                    className="calendar_moreButtonWrap"
                                    key={e.id}
                                    style={{
                                        top: e.top + 'px',
                                        left: e.leftPerc + '%',
                                        width: ((1 / 7) * 100) + '%'
                                    }}
                                >
                                    <button
                                        className="calendar_moreButton"
                                        onClick={() => onClickMoreButtonInternal(e.cellId)}
                                    >
                                        {e.count} more
                                    </button>
                                </div>
                            )
                        } else {
                            if (renderPassRef.current === 2 && e.isHidden) return null;

                            const style: React.CSSProperties = {
                                top: e.top + 'px',
                                left: e.leftPerc + '%',
                                right: e.rightPerc + '%',
                            };

                            // Only set heights on second pass because on the first pass we need to render them as-is 
                            // to see how tall each event is.
                            if (renderPassRef.current === 2) {
                                style.height = e.height + 'px';
                            }
                            const eventProps: ICalendarRenderProps = {
                                className: cn('calendar_event', generateEventClasses(e.origEvent, e)),
                                key: e.id,
                                onMouseEnter: () => onMouseEnterEvent(e),
                                onMouseLeave: () => onMouseLeaveEvent(e),
                                ref: eventRefs.current[e.id],
                                style: style,
                            };

                            return renderEvent(eventProps, e.origEvent, e);
                        }
                    })}
                </div>
            ))}
        </div>
        {showDayPanel && (() => {
            let dayPanelEvents = (selectedCell && eventsPerCell[selectedCell.id]) || [];

            // For grouped events, we don't want to render gap events if we have a non-gap event in the same group.
            const groupHasNonGapEvent: { [groupId: string]: boolean } = {};
            for (const e of dayPanelEvents) {
                if (!e.origEvent.isGroupGap) {
                    groupHasNonGapEvent[e.origEvent.groupId] = true;
                }
            }
            // Only include gap events if the group doesn't have a regular event on this day.
            dayPanelEvents = dayPanelEvents.filter(e => !e.origEvent.isGroupGap || !groupHasNonGapEvent[e.origEvent.groupId]);

            return (
                <div className="calendarDayPanel">
                    <div className="calendarDayPanel_header">
                        <div className="calendarDayPanel_headerText">
                            {selectedCell && selectedCell.dateDt.toLocaleString({
                                weekday: 'long',
                                month: 'short',
                                day: 'numeric',
                            })}
                        </div>
                        <button
                            className="calendarDayPanel_closeButton"
                            onClick={onCloseDayPanel}
                        >
                            <FontAwesomeIcon icon="times" />
                        </button>
                    </div>
                    <div className="calendarDayPanel_events">
                        {dayPanelEvents.map(e => {
                            const eventProps: ICalendarRenderProps = {
                                className: cn('calendar_eventDayPanel', generateEventClasses(e.origEvent, e, selectedCell)),
                                key: e.id,
                                onMouseEnter: () => onMouseEnterEvent(e),
                                onMouseLeave: () => onMouseLeaveEvent(e),
                            };
                            return renderEventDayPanel(eventProps, e.origEvent, e, selectedCell)
                        })}
                    </div>
                </div>
            );
        })()}
    </div>;
}